import React from 'react';
import { Music, GraduationCap, Rocket, Users, Lightbulb, Wrench, Trophy, Calendar, MessageCircle, UserCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const EventFormatSection: React.FC = () => {
  const pillars = [
    {
      icon: Music,
      title: 'Entertain',
      description: 'High-energy in person. Broadcast-quality online. One unforgettable experience.',
    },
    {
      icon: GraduationCap,
      title: 'Educate',
      description: 'Walk away with job-ready AI skills you can apply tomorrow.',
    },
    {
      icon: Rocket,
      title: 'Elevate',
      description: 'Create real impact for nonprofits while doing work that matters.',
    },
  ];

  const preEventTimeline = [
    {
      timing: '4+ Weeks Out',
      icon: UserCheck,
      title: 'Join a Team',
      description: 'Register and indicate your interests. Submit a team or be matched.',
    },
    {
      timing: '2-3 Weeks Out',
      icon: MessageCircle,
      title: 'Meet Partners',
      description: 'Understand challenges deeply. Gather requirements, start planning.',
    },
    {
      timing: '1 Week Out',
      icon: Calendar,
      title: 'Pre-Plan',
      description: 'Work with coaches to outline your approach and prepare.',
    },
  ];

  const dayOfTimeline = [
    { step: 1, icon: Users, label: 'Welcome', time: 'Doors Open' },
    { step: 2, icon: Lightbulb, label: 'Kickoff', time: 'Introductions' },
    { step: 3, icon: Wrench, label: 'Build', time: 'Main Event' },
    { step: 4, icon: Trophy, label: 'Showcase', time: 'Finale' },
  ];

  return (
    <section className="space-y-6 print:break-inside-avoid">
      <div>
        <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">
          The Experience
        </p>
        <h2 className="text-2xl md:text-3xl font-bold font-display">
          More Than a Hackathon
        </h2>
      </div>

      {/* Three Pillars */}
      <div className="grid md:grid-cols-3 gap-4">
        {pillars.map((pillar) => {
          const IconComponent = pillar.icon;
          return (
            <Card key={pillar.title} className="bg-card border border-border">
              <CardContent className="p-5 text-center">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <IconComponent className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold font-display mb-1">{pillar.title}</h3>
                <p className="text-sm text-muted-foreground">{pillar.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Event Flow */}
      <div className="bg-muted/20 rounded-xl p-5 border border-border space-y-5">
        <h3 className="text-sm font-semibold text-center uppercase tracking-wide text-muted-foreground">Event Flow</h3>
        
        {/* Pre-Event Timeline */}
        <div className="grid md:grid-cols-3 gap-3">
          {preEventTimeline.map((item) => {
            const IconComponent = item.icon;
            return (
              <div key={item.title} className="flex items-start gap-3 bg-card/50 rounded-lg p-3 border border-border/50">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <IconComponent className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-primary">{item.timing}</p>
                  <h4 className="font-semibold text-sm">{item.title}</h4>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs font-medium text-muted-foreground uppercase">Event Day</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Day-of Timeline */}
        <div className="grid grid-cols-4 gap-3">
          {dayOfTimeline.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div key={item.step} className="text-center">
                <div className="relative">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-1">
                    <IconComponent className="h-4 w-4 text-primary" />
                  </div>
                  {index < dayOfTimeline.length - 1 && (
                    <div className="hidden md:block absolute top-4 left-[60%] w-[80%] h-0.5 bg-border" />
                  )}
                </div>
                <p className="text-xs text-primary font-medium">{item.time}</p>
                <p className="text-xs font-medium">{item.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EventFormatSection;